import { useMemo, useRef, useState } from "react";
import type { FulfillmentOrderLine } from "../../types";
import { changedEntryFields, type EntryExistingDraft, type EntryParseResult, type EntryPreviewItem } from "../../service/confirmationEditorEntry";
import { calculateFulfillmentLineAmount, confirmationTravelerRoleLabel, sumFulfillmentAmounts } from "../../service/fulfillmentOrderEditorService";
import { confirmationBillingCountUnit } from "../../service/confirmationEditorView";
import { formatConfirmationAmount as amountText, formatConfirmationDecimal as compact } from "../../service/confirmationDecimalDisplay";
import { ConfirmationButton } from "./primitives";
import { EntrySupplierPicker } from "./EntryPickers";
import type { ConfirmationEntryProps } from "./types";
export type ConfirmationNewPreview = EntryPreviewItem & {
    previewKey: string;
};
export { confirmationTravelerRoleLabel } from "../../service/fulfillmentOrderEditorService";
/** 只压缩展示中无意义的单次因子，原量价与历史金额仍完整保留。 */
function costFormula(line: Partial<FulfillmentOrderLine>): string {
    const period = confirmationBillingCountUnit(line);
    if (line.unit === "项" && line.pricingUnitType === "unit" && Number(line.quantity) === 1 && Number(line.billingCount) === 1)
        return "整项金额";
    const count = period !== "次" || Number(line.billingCount) !== 1 ? ` × ${compact(line.billingCount)}${period}` : "";
    return `${compact(line.quantity)}${line.unit || ""}${count} × ${amountText(compact(line.unitPriceAmount))}`;
}
/** 新旧费用共用分类列，身份与修改方式独立保留。 */
export function crewEntryColumns(): string[] {
    return ["选择", "费用项目", "计算依据", "金额", "服务主体", "结算主体", "操作"];
}
/** 连续分类预览的主体分组，已有行以原 ID 回写，新行保留原文定位。 */
export function ConfirmationCrewEntryRows({ rows, newRows, issues, groupId, groupLabel, rangeLabel, active, onFocusGroup, onChange, runtime, disabled, selectedNew, onSelectNew, selectedExisting, onSelectExisting, onLocate, hideGroupHeading }: {
    rows: EntryExistingDraft[];
    newRows: ConfirmationNewPreview[];
    issues: EntryParseResult["errors"];
    groupId: string;
    groupLabel: string;
    rangeLabel: string;
    active: boolean;
    onFocusGroup: () => void;
    onChange: (rows: EntryExistingDraft[]) => void;
    runtime: ConfirmationEntryProps["runtime"];
    disabled: boolean;
    selectedNew: ReadonlySet<string>;
    onSelectNew: (key: string, selected: boolean) => void;
    selectedExisting?: ReadonlySet<string>;
    onSelectExisting?: (ids: string[], selected: boolean) => void;
    mealDays?: ReadonlyArray<{
        dayNo: number;
        date: string | null;
    }>;
    onMoveMeal?: (itemKey: string, targetDay: number) => void;
    onDragMeal?: (itemKey: string | null) => void;
    onLocate: (groupId: string, start: number, end: number, issue?: EntryParseResult["errors"][number]) => void;
    hideGroupHeading?: boolean;
}) {
    const [localSelected, setLocalSelected] = useState<Set<string>>(new Set());
    const sourceSelected = selectedExisting ?? localSelected;
    const selected = useMemo(() => new Set(rows.filter((row) => !row.removed && sourceSelected.has(row.base.id)).map((row) => row.base.id)), [rows, sourceSelected]);
    const selectExisting = (ids: string[], checked: boolean) => {
        if (disabled)
            return;
        if (onSelectExisting) {
            onSelectExisting(ids, checked);
            return;
        }
        setLocalSelected((previous) => {
            const next = new Set(previous);
            for (const id of ids) {
                if (checked)
                    next.add(id);
                else
                    next.delete(id);
            }
            return next;
        });
    };
    const settlementLocked = rows.some((row) => selected.has(row.base.id) && row.base.fulfillmentOrderId);
    const settlementContext = useRef({ selected, rows, disabled });
    settlementContext.current = { selected, rows, disabled };
    const columnCount = crewEntryColumns().length;
    const update = (ids: Set<string>, patch: Partial<FulfillmentOrderLine>) => {
        if (!disabled)
            onChange(rows.map((row) => !row.removed && ids.has(row.base.id) ? { ...row, patch: { ...row.patch, ...patch } } : row));
    };
    const records: Array<{
        row?: EntryExistingDraft;
        addition?: ConfirmationNewPreview;
        line: Partial<FulfillmentOrderLine>;
        amount: string;
    }> = rows.map((row) => {
        const changed = changedEntryFields(row);
        let amount = row.base.amount;
        if (["quantity", "billingCount", "unitPriceAmount"].some((name) => name in changed)) {
            const normalized = { ...row.base, ...changed };
            try {
                amount = calculateFulfillmentLineAmount(normalized.quantity, normalized.billingCount, normalized.unitPriceAmount);
            }
            catch {
                amount = "";
            }
        }
        return { row, line: { ...row.base, ...row.patch }, amount };
    });
    records.push(...newRows.map((addition) => ({ addition, line: addition.patch, amount: addition.patch.amount || "0" })));
    const subtotal = sumFulfillmentAmounts(records.filter((item) => !item.row?.removed).map((item) => item.amount || "0"));
    return <tbody className="confirmation-entry-group" data-entry-group={groupId} data-active={active || undefined}>
    <tr className="confirmation-entry-group-heading" data-compact={hideGroupHeading || undefined}><th colSpan={columnCount}><div>{!hideGroupHeading ? <><button type="button" onClick={onFocusGroup}>{groupLabel}</button><span>{rangeLabel}</span><strong>{amountText(subtotal)}</strong></> : <span>勾选费用可设置主体</span>}
      {rows.some((row) => !row.removed) ? <ConfirmationButton size="sm" variant="ghost" disabled={disabled} onClick={() => selectExisting(rows.filter((row) => !row.removed).map((row) => row.base.id), selected.size !== rows.filter((row) => !row.removed).length)}>全选本组</ConfirmationButton> : null}</div></th></tr>
    {selected.size ? <tr><td colSpan={columnCount}><div className="confirmation-entry-bulk"><span>已选 {selected.size} 行</span>
      {<EntrySupplierPicker label="批量服务主体" options={runtime.quickEntryFulfillmentSupplierOptions} onSearch={runtime.searchQuickEntryFulfillmentSuppliers} disabled={disabled} onChoose={(value) => update(selected, { fulfillmentSupplierId: value?.id ?? null, fulfillmentSupplierName: value?.name ?? null, ...({ providerAssignmentStatus: value ? "assigned" : "unassigned" }) })}/>}
      <EntrySupplierPicker key={JSON.stringify([...selected].sort())} label="批量结算主体" options={runtime.settlementSupplierOptions} onSearch={runtime.searchSettlementSuppliers} disabled={disabled || settlementLocked} onChoose={(value) => {
                const current = settlementContext.current;
                // 候选只提交打开时的完整选择；归口行由原协议决定结算，不能静默改写可编辑子集。
                if (current.disabled || current.selected !== selected || current.rows.some((row) => current.selected.has(row.base.id) && row.base.fulfillmentOrderId))
                    return;
                onChange(current.rows.map((row) => current.selected.has(row.base.id) ? { ...row, patch: { ...row.patch, settlementSupplierId: value?.id ?? null, settlementSupplierName: value?.name ?? null } } : row));
            }}/>
      {settlementLocked ? <span>已归口明细的结算主体由原协议决定，请取消勾选这些行后设置其他明细。</span> : null}
    </div></td></tr> : null}
    {records.map(({ row, line, amount, addition }) => {
            const subject = (kind: "fulfillment" | "settlement") => <span className={!line[`${kind}SupplierName`] ? "confirmation-entry-unset" : ""}>{line[`${kind}SupplierName`] || "待设置"}</span>;
            const itemTitle = line.itemNameSnapshot;
            const title = addition ? <button type="button" className="confirmation-entry-source" onClick={() => onLocate(groupId, addition.sourceStart, addition.sourceEnd)}>{itemTitle}</button> : <strong>{itemTitle}</strong>;
            return <tr key={row?.base.id || addition!.previewKey} className="confirmation-entry-detail confirmation-entry-cost-row" data-removed={row?.removed || undefined} data-preview-start={addition?.sourceStart} data-preview-end={addition?.sourceEnd} data-preview-group={addition ? groupId : undefined} data-preview-key={addition?.previewKey}><td colSpan={columnCount}>
          <div className="confirmation-entry-cost-meta"><input type="checkbox" aria-label={row ? `编辑选择 ${row.base.id}` : `选择新增 ${addition!.title} ${addition!.key}`} checked={row ? selected.has(row.base.id) : selectedNew.has(addition!.previewKey)} disabled={disabled || row?.removed} onChange={(event) => row ? selectExisting([row.base.id], event.target.checked) : onSelectNew(addition!.previewKey, event.target.checked)}/>{title}<span>{confirmationTravelerRoleLabel(line.travelerRole) || (line.unit === "项" ? "整项金额" : "指定数量")}</span><small>{row ? `第 ${row.base.lineNo} 行` : "新增费用"}</small></div>
          <div className="confirmation-entry-cost-formula" aria-label={`${itemTitle} 计算依据`}><span>{costFormula(line)} = </span><strong>{amountText(amount)}</strong></div>
          <div className="confirmation-entry-cost-subjects"><span>服务：{subject("fulfillment")}</span><span>结算：{subject("settlement")}</span><span>{row ? row.removed ? "删除" : Object.keys(changedEntryFields(row)).length ? "修改" : "保留" : "新增"}</span></div>
          {line.remark ? <p className="confirmation-entry-cost-remark">{line.remark}</p> : null}
        </td></tr>;
        })}
    {issues.map((issue) => <tr key={`${issue.sourceStart}-${issue.message}`} className="confirmation-entry-issue" data-preview-start={issue.sourceStart} data-preview-end={issue.sourceEnd} data-preview-group={groupId}><td colSpan={columnCount}><button type="button" onClick={() => onLocate(groupId, issue.sourceStart, issue.sourceEnd, issue)}><strong>{issue.message}</strong><span>{issue.raw}</span></button></td></tr>)}
    {!records.length && !issues.length ? <tr><td colSpan={columnCount} className="confirmation-entry-empty">{active ? "在左侧填写费用，预览将在此展开" : "本组尚未录入费用"}</td></tr> : null}
    {rows.some((row) => row.removed) ? <tr><td colSpan={columnCount} className="confirmation-entry-warning">应用时仅移除本组明确标记的行。</td></tr> : null}
  </tbody>;
}
